import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const PALETTE = [
  "#4E79A7",
  "#F28E2B",
  "#E15759",
  "#76B7B2",
  "#59A14F",
  "#EDC948",
  "#B07AA1",
  "#FF9DA7",
  "#9C755F",
  "#BAB0AC",
];

const GRID_COLOR = "#e5e7eb";
const TEXT_MID = "#6b7280";
const CHART_MARGIN = { top: 28, right: 24, bottom: 138, left: 84 };
const GROUPED_BAR_GAP = 2;
const GROUPED_BAR_CATEGORY_GAP = "18%";
const GROUPED_MIN_GROUP_SLOT = 84;
const GROUPED_MIN_BAR_WIDTH = 8;
const GROUPED_CHART_MIN_WIDTH = 920;
const GROUPED_MAX_GROUP_SLOT = 180;
const GROUPED_TICK_LINE_HEIGHT = 13;
const GROUPED_TICK_MIN_CHARS = 9;
const GROUPED_TICK_MAX_CHARS = 16;
const GROUPED_TICK_MAX_LINES = 4;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toLabel = (value, fallback = "Unknown") => {
  const normalized = String(value ?? "").trim();
  return normalized.length ? normalized : fallback;
};

const isNumericCodeLabel = (value) => /^-?\d+(\.\d+)?$/.test(String(value ?? "").trim());

const splitLongToken = (token, maxChars) => {
  const normalized = String(token ?? "");
  if (!normalized || normalized.length <= maxChars) return [normalized];

  const chunkSize = Math.max(2, maxChars - 1);
  const chunks = [];

  for (let index = 0; index < normalized.length; index += chunkSize) {
    const chunk = normalized.slice(index, index + chunkSize);
    const hasMore = index + chunkSize < normalized.length;
    chunks.push(hasMore ? `${chunk}-` : chunk);
  }

  return chunks;
};

const buildTickLines = (value, maxChars = 18, maxLines = 3) => {
  const normalized = toLabel(value, "(Blank)")
    .replace(/([/|])/g, "$1 ")
    .replace(/-/g, "- ");
  const words = normalized
    .split(/\s+/)
    .filter(Boolean)
    .flatMap((token) => splitLongToken(token, maxChars));
  if (!words.length) return ["(Blank)"];

  const lines = [];
  let index = 0;

  while (index < words.length && lines.length < maxLines) {
    let line = words[index];
    index += 1;

    while (index < words.length && `${line} ${words[index]}`.length <= maxChars) {
      line = `${line} ${words[index]}`;
      index += 1;
    }

    if (lines.length === maxLines - 1 && index < words.length) {
      const overflow = `${line} ${words.slice(index).join(" ")}`.trim();
      line =
        overflow.length > maxChars
          ? `${overflow.slice(0, Math.max(1, maxChars - 1))}...`
          : overflow;
      index = words.length;
    }

    lines.push(line);
  }

  return lines;
};

const WrappedAxisTick = ({
  x = 0,
  y = 0,
  payload,
  maxChars = 18,
  maxLines = 3,
  lineHeight = GROUPED_TICK_LINE_HEIGHT,
}) => {
  const lines = buildTickLines(payload?.value, maxChars, maxLines);

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <text
          key={`${line}-${index}`}
          x={0}
          y={12 + index * lineHeight}
          textAnchor="middle"
          fill={TEXT_MID}
          fontSize={11}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

WrappedAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  maxChars: PropTypes.number,
  maxLines: PropTypes.number,
  lineHeight: PropTypes.number,
};

const renderWrappedTick = (maxChars = 18, maxLines = 3, lineHeight = GROUPED_TICK_LINE_HEIGHT) => {
  return (props) => (
    <WrappedAxisTick {...props} maxChars={maxChars} maxLines={maxLines} lineHeight={lineHeight} />
  );
};

const formatPct = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(1)}%` : "0.0%";
};

const getRowWeight = (row) => {
  const numeric = Number(row?.weight);
  if (!Number.isFinite(numeric)) return 1;
  return Math.max(0, numeric);
};

const formatCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Math.abs(numeric - Math.round(numeric)) < 1e-6) return Math.round(numeric).toLocaleString();
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
};

const formatCountAxisTick = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Math.abs(numeric - Math.round(numeric)) < 1e-6) return Math.round(numeric).toLocaleString();
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const resolveSeriesDataKey = (seriesKey, yAxisMetric) =>
  yAxisMetric === "count" ? `${seriesKey}__count` : seriesKey;

const pickTooltipItem = (payload = [], mode) => {
  if (!Array.isArray(payload) || payload.length === 0) return {};
  if (mode === "outcome_only") return payload[0] || {};

  return (
    payload.find(
      (entry) => entry && typeof entry.dataKey === "string" && !entry.dataKey.endsWith("__count")
    ) ||
    payload.find((entry) => entry && entry.value != null) ||
    payload[0] ||
    {}
  );
};

const CountTooltip = ({ active, payload, mode }) => {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const item = pickTooltipItem(payload, mode);
  const source = item.payload || {};

  if (mode === "outcome_only") {
    const outcome = toLabel(source.outcome, toLabel(item.name, "Unknown option"));
    const count = formatCount(source.count);

    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
        <p className="font-semibold text-[#181010]">{outcome}</p>
        <p className="text-gray-600">Total Count: {count}</p>
        <p className="text-gray-600">Frequency: {formatPct(source.pct)}</p>
      </div>
    );
  }

  if (mode === "year_comparison") {
    const outcome = toLabel(source.outcome, "Unknown outcome");
    const year = toLabel(item.name, "Year");
    const dataKey = String(item.dataKey || "");
    const baseDataKey = dataKey.endsWith("__count") ? dataKey.slice(0, -7) : dataKey;
    const count = formatCount(source[`${baseDataKey}__count`]);
    const frequency = formatPct(source[baseDataKey]);

    return (
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
        <p className="font-semibold text-[#181010]">{outcome}</p>
        <p className="text-gray-600">Year: {year}</p>
        <p className="text-gray-600">Total Count: {count}</p>
        <p className="text-gray-600">Frequency: {frequency}</p>
      </div>
    );
  }

  const group = toLabel(source.group, "Unknown comparison group");
  const outcome = toLabel(item.name, "Unknown outcome");
  const dataKey = String(item.dataKey || "");
  const baseDataKey = dataKey.endsWith("__count") ? dataKey.slice(0, -7) : dataKey;
  const count = formatCount(source[`${baseDataKey}__count`]);
  const frequency = formatPct(source[baseDataKey]);

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-[#181010]">{group}</p>
      <p className="text-gray-600">Outcome: {outcome}</p>
      <p className="text-gray-600">Total Count: {count}</p>
      <p className="text-gray-600">Frequency: {frequency}</p>
    </div>
  );
};

CountTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  mode: PropTypes.oneOf(["grouped", "outcome_only", "year_comparison"]).isRequired,
};

const LollipopBarShape = ({ x, y, width, height, fill }) => {
  const safeX = Number(x) || 0;
  const safeY = Number(y) || 0;
  const safeWidth = Number(width) || 0;
  const safeHeight = Number(height) || 0;
  const cx = safeX + safeWidth / 2;
  const yBottom = safeY + safeHeight;

  return (
    <g>
      <line x1={cx} y1={yBottom} x2={cx} y2={safeY} stroke={fill} strokeWidth={2} />
      <circle cx={cx} cy={safeY} r={Math.max(3.5, Math.min(6, safeWidth / 2))} fill={fill} />
    </g>
  );
};

LollipopBarShape.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  fill: PropTypes.string,
};

const buildGroupedData = (rows = [], dependentAnswerMapping = null) => {
  const byGroup = new Map();
  const seenOutcomes = new Set();
  const groupDisplayByRaw = new Map();

  rows.forEach((row) => {
    const rawGroup = toLabel(row.groupRaw, toLabel(row.group, "No comparison category selected"));
    const displayGroup = toLabel(row.group, rawGroup);
    const existingDisplay = groupDisplayByRaw.get(rawGroup);

    if (
      !existingDisplay ||
      (isNumericCodeLabel(existingDisplay) && !isNumericCodeLabel(displayGroup))
    ) {
      groupDisplayByRaw.set(rawGroup, displayGroup);
    }
  });

  rows.forEach((row) => {
    const rawGroup = toLabel(row.groupRaw, toLabel(row.group, "No comparison category selected"));
    const group = toLabel(
      groupDisplayByRaw.get(rawGroup),
      toLabel(row.group, toLabel(row.groupRaw, "No comparison category selected"))
    );
    const outcome = toLabel(row.dep, toLabel(row.depRaw, "Unknown outcome"));
    const weight = getRowWeight(row);
    if (weight <= 0) return;

    seenOutcomes.add(outcome);

    if (!byGroup.has(group)) {
      byGroup.set(group, {
        total: 0,
        countsByOutcome: new Map(),
      });
    }

    const bucket = byGroup.get(group);
    bucket.total += weight;
    bucket.countsByOutcome.set(outcome, (bucket.countsByOutcome.get(outcome) || 0) + weight);
  });

  const presentOutcomes = Array.from(seenOutcomes);
  const presentOutcomeSet = new Set(presentOutcomes);
  const mappedOutcomeOrder = Object.entries(dependentAnswerMapping || {})
    .map(([rawValue, entry]) => toLabel(entry?.Display ?? entry?.display, toLabel(rawValue)))
    .filter((label) => presentOutcomeSet.has(label));
  const mappedSet = new Set(mappedOutcomeOrder);
  const fallbackOutcomes = presentOutcomes.filter((label) => !mappedSet.has(label));
  const outcomeOrder = [...mappedOutcomeOrder, ...fallbackOutcomes];

  const series = outcomeOrder.map((label, index) => ({
    key: `dep_${index}`,
    label,
  }));

  const groupedRows = Array.from(byGroup.entries())
    .map(([group, bucket]) => {
      const row = { group };
      series.forEach((item) => {
        const count = bucket.countsByOutcome.get(item.label) || 0;
        row[item.key] = bucket.total ? (count / bucket.total) * 100 : 0;
        row[`${item.key}__count`] = count;
      });
      return row;
    })
    .sort((a, b) => String(a.group).localeCompare(String(b.group)));

  const maxGroupedValue = groupedRows.reduce(
    (maxValue, row) =>
      Math.max(
        maxValue,
        ...series.map((item) => {
          const value = Number(row[item.key]);
          return Number.isFinite(value) ? value : 0;
        })
      ),
    0
  );

  if (maxGroupedValue > 0 && maxGroupedValue <= 1) {
    groupedRows.forEach((row) => {
      series.forEach((item) => {
        const value = Number(row[item.key]);
        row[item.key] = Number.isFinite(value) ? value * 100 : 0;
      });
    });
  }

  return { groupedRows, series };
};

const buildOutcomeOnlyData = (rows = []) => {
  const counts = new Map();
  let total = 0;
  rows.forEach((row) => {
    const outcome = toLabel(row.dep, toLabel(row.depRaw, "Unknown outcome"));
    const weight = getRowWeight(row);
    counts.set(outcome, (counts.get(outcome) || 0) + weight);
    total += weight;
  });

  const safeTotal = total || 1;
  return Array.from(counts.entries())
    .map(([outcome, count]) => ({
      outcome,
      count,
      pct: (count / safeTotal) * 100,
    }))
    .sort((a, b) => String(a.outcome).localeCompare(String(b.outcome)));
};

const buildOutcomeOnlyYearComparisonData = ({ yearComparisonSeries = [] }) => {
  const normalizedSeries = (yearComparisonSeries || [])
    .filter((seriesItem) => Array.isArray(seriesItem?.graphData) && seriesItem.graphData.length > 0)
    .map((seriesItem, index) => ({
      key: `year_${index}`,
      label: toLabel(seriesItem?.yearLabel, `Year ${index + 1}`),
      rows: buildOutcomeOnlyData(seriesItem.graphData),
    }));

  if (!normalizedSeries.length) {
    return { rows: [], series: [] };
  }

  const allOutcomes = [];
  const seenOutcomes = new Set();

  normalizedSeries.forEach((seriesItem) => {
    seriesItem.rows.forEach((row) => {
      if (seenOutcomes.has(row.outcome)) return;
      seenOutcomes.add(row.outcome);
      allOutcomes.push(row.outcome);
    });
  });

  const rows = allOutcomes.map((outcome) => {
    const row = { outcome };

    normalizedSeries.forEach((seriesItem) => {
      const match = seriesItem.rows.find((outcomeRow) => outcomeRow.outcome === outcome) || {
        pct: 0,
        count: 0,
      };
      row[seriesItem.key] = match.pct;
      row[`${seriesItem.key}__count`] = match.count;
    });

    return row;
  });

  const series = normalizedSeries.map(({ key, label }) => ({ key, label }));
  return { rows, series };
};

const AXIS_LABEL_COMMON = {
  fill: TEXT_MID,
  fontSize: 13,
  fontWeight: 600,
};

const renderGroupedBars = (series, chartType, yAxisMetric) => {
  const isLollipop = chartType === "lollipop";

  return series.map((item, index) => (
    <Bar
      key={item.key}
      dataKey={resolveSeriesDataKey(item.key, yAxisMetric)}
      name={item.label}
      fill={PALETTE[index % PALETTE.length]}
      radius={isLollipop ? [0, 0, 0, 0] : 0}
      maxBarSize={isLollipop ? 10 : undefined}
      minPointSize={isLollipop ? 0 : 1}
      shape={isLollipop ? <LollipopBarShape /> : undefined}
      isAnimationActive={false}
    />
  ));
};

const ChartRechartsBarCategorical = ({
  graphData,
  chartType,
  yAxisMetric,
  hasComparison,
  dependentAnswerMapping,
  selectedIndepMeta,
  selectedDepMeta,
  yearComparisonSeries,
  yearComparisonEnabled,
}) => {
  const { groupedRows, series } = useMemo(
    () => buildGroupedData(graphData, dependentAnswerMapping),
    [graphData, dependentAnswerMapping]
  );
  const isOutcomeOnly = !hasComparison;
  const isYearComparisonOutcomeOnly =
    isOutcomeOnly && yearComparisonEnabled && yearComparisonSeries.length > 1;
  const outcomeOnlyRows = useMemo(
    () => (isOutcomeOnly ? buildOutcomeOnlyData(graphData) : []),
    [isOutcomeOnly, graphData]
  );
  const { rows: outcomeYearComparisonRows, series: outcomeYearComparisonSeries } = useMemo(
    () =>
      isYearComparisonOutcomeOnly
        ? buildOutcomeOnlyYearComparisonData({
            yearComparisonSeries,
          })
        : { rows: [], series: [] },
    [isYearComparisonOutcomeOnly, yearComparisonSeries]
  );
  const groupedLayout = useMemo(() => {
    if (isOutcomeOnly) {
      return {
        groupedChartMinWidth: GROUPED_CHART_MIN_WIDTH,
        groupedTickMaxChars: 16,
        groupedTickMaxLines: 3,
        groupedXAxisHeight: 92,
        groupedBottomMargin: CHART_MARGIN.bottom,
      };
    }

    const seriesCount = Math.max(1, series.length);
    const groupsCount = Math.max(1, groupedRows.length);
    const longestGroupLabel = groupedRows.reduce((maxLength, row) => {
      const length = String(toLabel(row.group, "")).length;
      return Math.max(maxLength, length);
    }, 0);

    const perGroupFromSeries =
      seriesCount * GROUPED_MIN_BAR_WIDTH + Math.max(0, seriesCount - 1) * GROUPED_BAR_GAP + 16;
    const perGroupFromLabel = clamp(
      longestGroupLabel * 5 + 24,
      GROUPED_MIN_GROUP_SLOT,
      GROUPED_MAX_GROUP_SLOT
    );
    const groupSlot = Math.max(perGroupFromSeries, perGroupFromLabel);

    const groupedTickMaxChars = clamp(
      Math.floor((groupSlot - 12) / 6.4),
      GROUPED_TICK_MIN_CHARS,
      GROUPED_TICK_MAX_CHARS
    );
    const groupedTickMaxLines =
      longestGroupLabel > groupedTickMaxChars * 2 ? GROUPED_TICK_MAX_LINES : 3;
    const groupedXAxisHeight = 44 + groupedTickMaxLines * GROUPED_TICK_LINE_HEIGHT;
    const groupedBottomMargin = Math.max(
      CHART_MARGIN.bottom,
      92 + groupedTickMaxLines * GROUPED_TICK_LINE_HEIGHT
    );

    const groupedChartMinWidth = Math.max(
      GROUPED_CHART_MIN_WIDTH,
      Math.ceil(groupsCount * groupSlot + CHART_MARGIN.left + CHART_MARGIN.right)
    );

    return {
      groupedChartMinWidth,
      groupedTickMaxChars,
      groupedTickMaxLines,
      groupedXAxisHeight,
      groupedBottomMargin,
    };
  }, [groupedRows, isOutcomeOnly, series.length]);

  if (isOutcomeOnly && !isYearComparisonOutcomeOnly && outcomeOnlyRows.length === 0) return null;
  if (isYearComparisonOutcomeOnly && outcomeYearComparisonRows.length === 0) return null;
  if (!isOutcomeOnly && (!groupedRows.length || !series.length)) return null;

  const xAxisLabel = isOutcomeOnly
    ? selectedDepMeta?.question || selectedDepMeta?.label || "Outcome"
    : selectedIndepMeta?.question || selectedIndepMeta?.label || "Comparison Category";

  const isLollipop = chartType === "lollipop";
  const usesCountAxis = yAxisMetric === "count";
  const yAxisLabel = usesCountAxis ? "Total Count" : "Frequency (%)";
  const yAxisTickFormatter = usesCountAxis ? formatCountAxisTick : (value) => `${value}%`;

  return (
    <div className="h-[620px] w-full">
      {isOutcomeOnly ? (
        isYearComparisonOutcomeOnly ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={outcomeYearComparisonRows}
              margin={CHART_MARGIN}
              barGap={GROUPED_BAR_GAP}
              barCategoryGap={GROUPED_BAR_CATEGORY_GAP}
            >
              <CartesianGrid strokeDasharray="4 4" stroke={GRID_COLOR} vertical={false} />
              <XAxis
                dataKey="outcome"
                interval={0}
                height={92}
                tickMargin={14}
                tick={renderWrappedTick(18, 3)}
                label={{
                  value: xAxisLabel,
                  position: "insideBottom",
                  offset: -2,
                  ...AXIS_LABEL_COMMON,
                }}
              />
              <YAxis
                width={66}
                tickMargin={8}
                domain={usesCountAxis ? [0, "auto"] : [0, 100]}
                allowDecimals={!usesCountAxis}
                tickFormatter={yAxisTickFormatter}
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  dx: -18,
                  ...AXIS_LABEL_COMMON,
                }}
              />
              <Tooltip
                shared={false}
                cursor={{ fill: "rgba(24, 16, 16, 0.06)" }}
                content={<CountTooltip mode="year_comparison" />}
              />
              <Legend verticalAlign="top" height={42} wrapperStyle={{ paddingBottom: 8 }} />
              {renderGroupedBars(outcomeYearComparisonSeries, chartType, yAxisMetric)}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={outcomeOnlyRows} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="4 4" stroke={GRID_COLOR} vertical={false} />
              <XAxis
                dataKey="outcome"
                interval={0}
                height={92}
                tickMargin={14}
                tick={renderWrappedTick(18, 3)}
                label={{
                  value: xAxisLabel,
                  position: "insideBottom",
                  offset: -2,
                  ...AXIS_LABEL_COMMON,
                }}
              />
              <YAxis
                width={66}
                tickMargin={8}
                domain={usesCountAxis ? [0, "auto"] : [0, 100]}
                allowDecimals={!usesCountAxis}
                tickFormatter={yAxisTickFormatter}
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  dx: -18,
                  ...AXIS_LABEL_COMMON,
                }}
              />
              <Tooltip
                cursor={{ fill: "rgba(24, 16, 16, 0.06)" }}
                content={<CountTooltip mode="outcome_only" />}
              />
              <Bar
                dataKey={usesCountAxis ? "count" : "pct"}
                name={usesCountAxis ? "Total Count" : "Frequency"}
                fill={PALETTE[0]}
                radius={isLollipop ? [0, 0, 0, 0] : [4, 4, 0, 0]}
                maxBarSize={isLollipop ? 12 : 72}
                shape={isLollipop ? <LollipopBarShape /> : undefined}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        )
      ) : (
        <div className="h-full w-full overflow-x-auto">
          <div className="h-full" style={{ minWidth: groupedLayout.groupedChartMinWidth }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={groupedRows}
                margin={{ ...CHART_MARGIN, bottom: groupedLayout.groupedBottomMargin }}
                barGap={GROUPED_BAR_GAP}
                barCategoryGap={GROUPED_BAR_CATEGORY_GAP}
              >
                <CartesianGrid strokeDasharray="4 4" stroke={GRID_COLOR} vertical={false} />
                <XAxis
                  dataKey="group"
                  interval={0}
                  height={groupedLayout.groupedXAxisHeight}
                  tickMargin={14}
                  tick={renderWrappedTick(
                    groupedLayout.groupedTickMaxChars,
                    groupedLayout.groupedTickMaxLines
                  )}
                  label={{
                    value: xAxisLabel,
                    position: "insideBottom",
                    offset: -2,
                    ...AXIS_LABEL_COMMON,
                  }}
                />
                <YAxis
                  width={66}
                  tickMargin={8}
                  domain={usesCountAxis ? [0, "auto"] : [0, 100]}
                  allowDecimals={!usesCountAxis}
                  tickFormatter={yAxisTickFormatter}
                  label={{
                    value: yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    dx: -18,
                    ...AXIS_LABEL_COMMON,
                  }}
                />
                <Tooltip
                  shared={false}
                  cursor={{ fill: "rgba(24, 16, 16, 0.06)" }}
                  content={<CountTooltip mode="grouped" />}
                />
                <Legend verticalAlign="top" height={42} wrapperStyle={{ paddingBottom: 8 }} />
                {renderGroupedBars(series, chartType, yAxisMetric)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

ChartRechartsBarCategorical.propTypes = {
  graphData: PropTypes.arrayOf(PropTypes.object).isRequired,
  chartType: PropTypes.oneOf(["grouped_bar", "lollipop"]).isRequired,
  yAxisMetric: PropTypes.oneOf(["frequency", "count"]).isRequired,
  hasComparison: PropTypes.bool.isRequired,
  dependentAnswerMapping: PropTypes.object,
  selectedIndepMeta: PropTypes.shape({
    question: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedDepMeta: PropTypes.shape({
    question: PropTypes.string,
    label: PropTypes.string,
  }),
  yearComparisonEnabled: PropTypes.bool,
  yearComparisonSeries: PropTypes.arrayOf(
    PropTypes.shape({
      yearLabel: PropTypes.string.isRequired,
      graphData: PropTypes.arrayOf(PropTypes.object).isRequired,
    })
  ),
};

ChartRechartsBarCategorical.defaultProps = {
  yearComparisonSeries: [],
  yearComparisonEnabled: false,
};

export default memo(ChartRechartsBarCategorical);
